// src/padplanner/PlanPage.jsx
//
// /padplanner/plans/$planId, mockups 4 and 5: a 370px panel with the plan
// (Review | Layout | Details | Wells | Templates) beside a full-height map
// with a toolbar, a readout, a scale bar and the imagery time strip, each in
// its MapHud.jsx slot. The versions strip is in the panel's head: which
// version is shown is the page's state (the Review tab, the
// layout, the comments), not the map's. The S4 editor is /plans/$planId/edit, opened
// from the head's Open editor, which is shown only when the server says
// can_edit. ?tab= opens a tab (the editor's "Add wells" link sends ?tab=wells).
// A draft opens on Details; a plan that has been submitted opens on Review.
//
// ── THE LAYOUT IS THE EDITOR'S, READ-ONLY (S6a2) ───────────────────────────
//
// The map draws the viewed revision's features, labels, wellhead markers,
// witness lines and redzone halos through planLayout.js's mountLayout with
// readOnly: the editor's own FootprintEditor and ChecksLayers, not a second
// drawing. PlanLayout.parity.test.jsx holds the two pages to the same map
// data for a draft, a frozen revision and a read-only viewer. A click on a
// feature shows its details (LayoutPanels.jsx FeatureDetails) and changes
// nothing. Any revision can be viewed (the versions strip); its features and
// checks are GET …/revisions/:rev_no/features and /checks, and a frozen
// revision's checks are the run it was submitted with. The halos follow the
// rules that run was checked against; a draft's follow the published set.
//
// ── THE REVIEW (S6a2, mockup 5) ────────────────────────────────────────────
//
// The lifecycle stepper is in the PadPlanner strip, right-aligned, for the
// version shown, or "Archived" (2026-10-31). The head has Submit (the editor's head has the same
// SubmitButton), Start revision, Download GeoJSON and, on a version issued
// with one, Download report (S7c; planLifecycle.js headActions: an issued
// plan's head is not empty). The Review tab has the
// route, the viewed revision's approval chain, the decision box, Issue, and
// the revision's comments, pinned on the map. planLifecycle.js decides what is
// offered; S6a1 decides what is done, and its refusals are shown by code.
// On a frozen revision Open editor is hidden (the server's can_edit is
// false) and the head says why; the editor's URL says it is read-only.
//
// ── THE PERIMETER HAS THREE SOURCES AND ONE SAVE ───────────────────────────
//
// Drawn on the map, parsed from an uploaded file, or copied from a detected
// pad. Each lands as a PENDING outline, shown on the map with its area, and a
// floating Save / Discard bar above the time strip; nothing is written until
// Save. The server measures it again and may refuse it (each cap has its own
// code, decision 9; planRefusals.js has the words). A pad is saved by its
// observation id and the server copies the geometry, so the client never
// asserts a pad's shape. Only the current revision's perimeter is offered.
//
// ── THE IMAGERY PIN IS THE SERVER'S RESOLUTION ─────────────────────────────
//
// The time strip shows the collects that cover the plan; choosing one shows
// it, and "Pin" asks the server to resolve that exact dataset for the plan.
// The server refuses a key it would not resolve itself (decision 1), so what
// is pinned is what the resolver says, not what the client chose.
//
// ── READ-ONLY IS THE SAME PAGE WITHOUT ITS CONTROLS ────────────────────────
//
// can_edit comes from the server: false for a CONSUMER_VIEWER or FIELD seat,
// for an archived plan and for a frozen revision. Controls that cannot work
// are not rendered at all rather than shown disabled; the Detected pads
// toggle, which only shows, stays.
//
// ── THE LANDOWNER (S6b2) ───────────────────────────────────────────────────
//
// The Review tab's Landowner section (LandownerSection.jsx) says where the
// plan went and what came back, and submits the version on screen. The
// landowner's findings are their own map layer (LandownerPins.js), toggled
// as "Landowner findings" under Map pins (Review and Layout tabs), next to
// Comment pins. Picking a finding shows the version it was made on and
// selects its feature there: a later version's copy of that feature has a
// new id (155's source_feature_id, which no read returns yet), so it is not
// guessed.
//
// The plan's intended landowner (158), when it names one, is a line in the
// head: "Landowner: <name> · not yet submitted" or its latest case's status,
// and why when the relationship is gone. Edit details changes it while the
// plan is a draft never submitted to a landowner (IntendedLandownerField.jsx).
//
// ── CUT ────────────────────────────────────────────────────────────────────
//
//   S4    placing templates, snap, align and the selected-object editing
//         panel: the editor's (EditorPage.jsx), not this page's
//   S6a2  "Changed since vN", the ghost of the last version, Compare, Drone
//         scan, As-built, Shared-with
//   --    "1 in = 40 ft": MapLibre's scale bar instead
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import PadPlannerGate from "./PadPlannerGate";
import PadPlannerMap from "./PadPlannerMap";
import SidePanel, { SectionLabel } from "./SidePanel";
import TemplatesTab from "./TemplatesTab";
import StatusPill from "./StatusPill";
import ChecksTab, { LayoutChecksPanel } from "./ChecksTab";
import CommentPins from "./CommentPins";
import LandownerPins, { pinAt } from "./LandownerPins";
import LandownerSection from "./LandownerSection";
import { findingsInView, intendedLandownerLine, intendedLocked, INTENDED_CHOOSE_TEXT } from "./landowner";
import IntendedLandownerField from "./IntendedLandownerField";
import { findingNumber } from "../portal/portalFindingNumber";
import { LayersTab, MapPinLayers, FeatureDetails } from "./LayoutPanels";
import {
  CompactStepper, SubmitButton, RouteRow, ApprovalChain, DecisionBox, LifecycleActions, VersionsStrip, Comments,
} from "./PlanReview";
import { ImageryStrip } from "../components/imagery/TimeStrip";
import { selectAndZoom, sitePlannerRows } from "./imageryStripRows";
import { NavEnd } from "./PadPlannerNav";
import {
  getPlan, updatePlan, archivePlan, updateRevision, putWell, deleteWell, lookupWells, getPad,
  parsePerimeterFile, getApproval, getRevisionFeatures, getRevisionChecks, runChecks, listComments, exportPlanGeoJson, getPlanReport, generatePlanReport,
  startRevision, getLandownerSubmissions, getLandownerRelationships,
} from "../api/padplannerApi";
import { useAuthStore } from "../stores/useAuthStore";
import { usePlanProjects, PROJECTS_FAILED_TEXT } from "./usePlanProjects";
import {
  PLAN_TYPES, PLAN_TYPE_LABELS, formatAcres, formatApi10, formatDate, formatSaved, formatUpdated, imageryLabel,
  imageryEmptyText, includeForCapture, referenceForCapture, creditForCapture,
} from "./planVocabulary";
import { usePlanLanes, openingImagery } from "./usePlanImagery";
import { usePlanTemplates, useRuleSets } from "./usePlanLibrary";
import { mountLayout, drawRedzones, frameResult, layoutRuleSetId, featureName } from "./planLayout";
import {
  decisionBoxFor, headActions, reportFor, reportTitle, startRevisionConfirm, startedText, FROZEN_TEXT, olderVersionText,
  generateReportConfirm, generatedReportText, shownStatus,
} from "./planLifecycle";
import { refusalText } from "./planRefusals";
import { peekNotice, dropNotice } from "./planNotices";
import { editablePolygon } from "./planMapData";
import ReadFailed from "./ReadFailed";
import { useMapView, useEditorDrawingStyle } from "./mapView";
import { provisionalEpsg, createFrame, isUtmEpsg } from "../lib/footprint-editor/frame.js";
import { padAxis } from "../lib/footprint-editor/padAxis.js";
import { usePadAlign } from "./padAlign";
import { PADPLANNER_DISPLAY_NAME } from "../constants/analyticsProducts";

// A note over the map: readable on imagery.
const MAP_NOTE = "rounded-md border border-bg-border bg-bg/95 px-3 py-1.5 shadow";

export const PLAN_TABS = Object.freeze(["review", "layout", "details", "wells", "templates"]);

export default function PlanPage({ planId }) {
  return (
    <PadPlannerGate section="plans">
      <Plan planId={planId} />
    </PadPlannerGate>
  );
}

const field = "rounded border border-bg-border bg-bg px-2 py-1.5 text-[13px] text-fg-primary";
const tabClass = (on) =>
  `-mb-px border-b-2 px-2.5 py-1.5 text-[12.5px] ${on ? "border-accent-action text-fg-primary" : "border-transparent text-fg-secondary hover:text-fg-primary"}`;
const TAB_LABELS = { review: "Review", layout: "Layout", details: "Details", wells: "Wells", templates: "Templates" };

function Plan({ planId }) {
  const navigate = useNavigate();
  const meId = useAuthStore((s) => s.profile?.id ?? null);
  const [loaded, setLoaded] = useState({ id: null, plan: null, canEdit: false, error: null });
  // New plan's account of a create that only partly succeeded (planNotices.js).
  const [notice, setNotice] = useState(() => peekNotice(planId));
  useEffect(() => { dropNotice(planId); }, [planId]);
  const search = useSearch({ strict: false });
  // null: the plan's own opening tab (Details for a draft, Review once submitted).
  const [tab, setTab] = useState(() => (PLAN_TABS.includes(search?.tab) ? search.tab : null));
  const [editing, setEditing] = useState(false);
  // Bumped after a lifecycle write: the plan, its approval and the revision shown are read again.
  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    let cancelled = false;
    getPlan(planId)
      .then((r) => { if (!cancelled) setLoaded({ id: planId, plan: r.plan, canEdit: r.can_edit === true, error: null }); })
      .catch((e) => { if (!cancelled) setLoaded({ id: planId, plan: null, canEdit: false, error: e.status === 404 ? "Plan not found." : e.message }); });
    return () => { cancelled = true; };
  }, [planId, version]);

  const [approvalState, setApprovalState] = useState({ key: null, approval: null, error: null });
  useEffect(() => {
    let cancelled = false;
    const key = `${planId}:${version}`;
    getApproval(planId)
      .then((a) => { if (!cancelled) setApprovalState({ key, approval: a, error: null }); })
      .catch((e) => { if (!cancelled) setApprovalState({ key, approval: null, error: e }); });
    return () => { cancelled = true; };
  }, [planId, version]);
  // A refresh keeps the last answer on screen until the new one arrives. A
  // failed read is said (the Review tab and the versions slot), never drawn
  // as a plan with no route, no chain and no versions.
  const approvalFor = approvalState.key?.startsWith(`${planId}:`) ? approvalState : null;
  const approval = approvalFor?.approval ?? null;
  const approvalError = approvalFor?.error ?? null;

  // Every write answers with the plan as it now is; adopt it and keep can_edit.
  const adopt = useCallback((r) => { if (r?.plan) setLoaded((l) => ({ ...l, plan: r.plan })); }, []);

  const current = loaded.id === planId ? loaded : null;
  if (current?.error) return <div className="p-6 text-sm text-fg-secondary" role="alert">{current.error}</div>;
  if (!current?.plan) return null;
  return (
    <PlanLoaded key={planId} plan={current.plan} canEdit={current.canEdit} approval={approval} approvalError={approvalError} meId={meId}
      tab={tab ?? (current.plan.status === "draft" ? "details" : "review")} setTab={setTab}
      editing={editing} setEditing={setEditing} notice={notice} setNotice={setNotice}
      adopt={adopt} refresh={refresh} version={version}
      onArchived={(r) => { adopt(r); setLoaded((l) => ({ ...l, canEdit: false })); }}
      onNavigate={(to, params) => navigate(params ? { to, params } : { to })} />
  );
}

function PlanLoaded({
  plan, canEdit, approval, approvalError, meId, tab, setTab, editing, setEditing, notice, setNotice, adopt, refresh, version, onArchived, onNavigate,
}) {
  const currentRev = plan.current_revision;
  // The version shown: null follows the current one (as a new revision starts).
  const [viewed, setViewed] = useState(null);
  const revNo = viewed !== null && viewed <= currentRev ? viewed : currentRev;
  const onCurrent = revNo === currentRev;

  // The viewed revision's features and checks, read again whenever the plan is.
  const [revData, setRevData] = useState({ key: null, features: null, checks: null });
  const [revAttempt, setRevAttempt] = useState(0);
  const revKey = `${revNo}:${version}:${plan.updated_at}:${revAttempt}`;
  useEffect(() => {
    let cancelled = false;
    Promise.all([getRevisionFeatures(plan.id, revNo), getRevisionChecks(plan.id, revNo)])
      .then(([f, c]) => {
        if (cancelled) return;
        setRevData({ key: revKey, revNo, features: f, checks: {
          loaded: true, error: null, run: c.run, rules: c.rules, results: c.results ?? [], full: true,
          publishedId: c.rule_set?.id ?? null, revision: c.revision,
        } });
      })
      .catch((e) => {
        if (!cancelled) setRevData({ key: revKey, revNo, features: null, error: e, checks: { loaded: true, error: refusalText(e), run: null, rules: null, results: [], full: true } });
      });
    return () => { cancelled = true; };
  }, [plan.id, revNo, revKey]);
  // The last answer for this revision stays until the next arrives (no flash on refresh).
  const shown = revData.revNo === revNo ? revData : { features: null, checks: null };
  const fdata = shown.features;
  const layoutError = shown.error ?? null;
  const checks = shown.checks ?? { loaded: false, error: null, run: null, rules: null, results: [], full: false };
  const frozen = fdata?.revision?.frozen === true;

  // Re-run checks (POST …/checks): an editor, on the current, unfrozen
  // revision. The run replaces the checks shown, so Submit follows its
  // verdict at once (a pass, warnings or not_checked enables it).
  const canRerun = canEdit && onCurrent && !frozen && !plan.archived_at;
  const [rerun, setRerun] = useState({ busy: false, error: null });
  async function rerunChecks() {
    setRerun({ busy: true, error: null });
    try {
      const r = await runChecks(plan.id);
      const run = r.checks.run;
      setRevData((d) => (d.revNo !== revNo ? d : {
        ...d, checks: { ...d.checks, loaded: true, error: null, run, rules: run.rules ?? d.checks?.rules ?? null, results: r.checks.results ?? [], full: true },
      }));
      setRerun({ busy: false, error: null });
    } catch (err) { setRerun({ busy: false, error: err }); }
  }

  const { library, templatesById, templates, retry: retryTemplates } = usePlanTemplates(plan, true);
  const { ruleSets, failed: rulesFailed, retry: retryRules } = useRuleSets([checks.publishedId ?? null, checks.run?.rule_set_id ?? null]);
  const rulesById = useMemo(() => new Map([...ruleSets.values()].flat().map((r) => [r.id, r])), [ruleSets]);
  const haloSetId = layoutRuleSetId({ frozen, run: checks.run, publishedId: checks.publishedId });
  const haloRules = (haloSetId && ruleSets.get(haloSetId)) || null;

  /* ── the layout on the map ─────────────────────────────────────────────── */

  const [map, setMap] = useState(null);
  const layoutRef = useRef(null);
  const [layout, setLayout] = useState(null);
  const [, setTick] = useState(0);
  const rerender = useCallback(() => setTick((t) => t + 1), []);
  const [selectedId, setSelectedId] = useState(null);
  const [hidden, setHidden] = useState(() => new Set());
  const [overlays, setOverlays] = useState({ layout: true, redzones: true, pins: true, landowner: true });
  const onOverlay = useCallback((patch) => setOverlays((o) => ({ ...o, ...patch })), []);
  const [interacting, setInteracting] = useState(false);
  // What a new layout starts with, read when the map or revision changes.
  const live = useRef({ templates: [], hidden: new Set() });
  useLayoutEffect(() => { live.current = { templates, hidden }; });

  const onMapReady = useCallback((m) => {
    if (!m) { layoutRef.current?.destroy(); layoutRef.current = null; setLayout(null); }
    setMap(m);
  }, []);

  // One layout per map and revision: a new version shown is a new drawing.
  useEffect(() => {
    if (!map || !fdata) return undefined;
    const l = mountLayout({
      map, readOnly: true, localEpsg: fdata.revision.local_epsg, perimeter: fdata.revision.perimeter,
      templates: live.current.templates, features: fdata.features,
      scaleBar: null,   // MapLibre's scale bar is the page's
      onSelectionChange: (ids) => setSelectedId(ids[0] ?? null),
    });
    for (const layer of live.current.hidden) l.editor.setLayerVisibility(layer, false);
    layoutRef.current = l;
    setLayout(l);
    return () => {
      l.destroy();
      if (layoutRef.current === l) layoutRef.current = null;
      setLayout((x) => (x === l ? null : x));
      setSelectedId(null);
    };
  }, [map, fdata]);

  useEffect(() => { layout?.editor.setTemplates(templates); }, [layout, templates]);
  useEffect(() => { layout?.checks.setResults(checks.results); }, [layout, checks.results]);
  useEffect(() => { layout?.checks.setVisible(overlays); }, [layout, overlays]);
  useEffect(() => { drawRedzones(layout, { rules: haloRules, templatesById }); }, [layout, haloRules, templatesById, templates]);

  /* ── comments ──────────────────────────────────────────────────────────── */

  const [commentState, setCommentState] = useState({ key: null, list: null, error: null });
  const commentsKey = `${revNo}:${version}`;
  const [commentsBump, setCommentsBump] = useState(0);
  useEffect(() => {
    let cancelled = false;
    listComments(plan.id, revNo)
      .then((r) => { if (!cancelled) setCommentState({ key: commentsKey, revNo, list: r, error: null }); })
      .catch((e) => { if (!cancelled) setCommentState({ key: commentsKey, revNo, list: null, error: e }); });
    return () => { cancelled = true; };
  }, [plan.id, revNo, commentsKey, commentsBump]);
  const comments = commentState.revNo === revNo ? commentState.list : null;
  const commentsError = commentState.revNo === revNo ? commentState.error : null;
  const reloadComments = useCallback(() => setCommentsBump((n) => n + 1), []);

  // Pinning and the selected pin belong to the version they were made on.
  const [pinState, setPinState] = useState({ revNo: null, pinning: false, place: null, selected: null });
  const pins = pinState.revNo === revNo ? pinState : { pinning: false, place: null, selected: null };
  const setPins = (patch) => setPinState({ ...pins, revNo, ...patch });

  const pinsRef = useRef(null);
  const pinsPick = useRef(null);
  useEffect(() => {
    if (!map) return undefined;
    const p = new CommentPins({ map, onPick: (id) => pinsPick.current?.(id) });
    pinsRef.current = p;
    return () => { p.destroy(); if (pinsRef.current === p) pinsRef.current = null; };
  }, [map]);
  useEffect(() => { pinsRef.current?.set(comments?.comments ?? [], { selectedId: pins.selected }); }, [map, comments, pins.selected]);
  useEffect(() => { pinsRef.current?.setPending(pins.place?.lngLat ?? null); }, [map, pins.place]);
  useEffect(() => { pinsRef.current?.setVisible(overlays.pins); }, [map, overlays.pins]);

  /* ── the landowner (S6b2) ─────────────────────────────────────────────── */

  const [landowner, setLandowner] = useState({ key: null, cases: null, casesError: null, relationships: null, relationshipsError: null });
  const [landownerBump, setLandownerBump] = useState(0);
  const landownerKey = `${version}:${landownerBump}`;
  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([getLandownerSubmissions(plan.id), getLandownerRelationships()]).then(([c, r]) => {
      if (cancelled) return;
      setLandowner({
        key: landownerKey,
        cases: c.status === "fulfilled" ? c.value.cases : null, casesError: c.status === "rejected" ? c.reason : null,
        relationships: r.status === "fulfilled" ? r.value : null, relationshipsError: r.status === "rejected" ? r.reason : null,
      });
    });
    return () => { cancelled = true; };
  }, [plan.id, landownerKey]);
  const reloadLandowner = useCallback(() => setLandownerBump((n) => n + 1), []);
  const inView = useMemo(() => findingsInView(landowner.cases ?? [], revNo), [landowner.cases, revNo]);

  // The finding picked: {id, revNo, featureId}. Its feature is selected once its version's layout is drawn.
  const [finding, setFinding] = useState(null);
  const landownerPinsRef = useRef(null);
  const landownerPick = useRef(null);
  useEffect(() => {
    if (!map) return undefined;
    const p = new LandownerPins({ map, onPick: (id) => landownerPick.current?.(id) });
    landownerPinsRef.current = p;
    return () => { p.destroy(); if (landownerPinsRef.current === p) landownerPinsRef.current = null; };
  }, [map]);
  useEffect(() => {
    // A finding with no place of its own sits on its feature, when that feature is on screen (its own version).
    const ed = layout?.editor ?? null;
    const at = (f) => {
      const own = pinAt(f.geometry);
      if (own) return own;
      const feat = f.rev_no === revNo && f.pp_feature_id ? ed?.getFeature(f.pp_feature_id) : null;
      return feat ? (feat.anchor?.coordinates ?? pinAt(feat.line_geom)) : null;
    };
    landownerPinsRef.current?.set(inView.findings.map((f) => ({
      id: f.id, n: findingNumber(f), lngLat: at(f), title: `${f.owner_org_name} · ${f.case_number ?? ""} · ${f.markup_type}`,
    })), { selectedId: finding?.id ?? null });
  }, [map, layout, inView, revNo, finding]);
  useEffect(() => { landownerPinsRef.current?.setVisible(overlays.landowner); }, [map, overlays.landowner]);
  useEffect(() => {
    const ed = layout?.editor;
    if (!finding || finding.revNo !== revNo || !finding.featureId || !ed?.getFeature(finding.featureId)) return;
    ed.select([finding.featureId]);
  }, [layout, finding, revNo]);

  /** A landowner finding picked, from its pin or the list: its version on screen, its feature selected. */
  function selectFinding(f) {
    setFinding({ id: f.id, revNo: f.rev_no, featureId: f.pp_feature_id ?? null });
    if (f.rev_no != null && f.rev_no !== revNo) setViewed(f.rev_no === currentRev ? null : f.rev_no);
  }
  useLayoutEffect(() => {
    landownerPick.current = (id) => { const f = inView.findings.find((x) => x.id === id); if (f) selectFinding(f); };
  });

  /** A comment picked, from its pin or the list: select it, and the feature it is on. */
  function selectComment(id) {
    setPins({ selected: id });
    const c = comments?.comments?.find((x) => x.id === id);
    const ed = layoutRef.current?.editor;
    if (c?.feature_id && ed?.getFeature(c.feature_id)) ed.select([c.feature_id]);
  }
  useLayoutEffect(() => { pinsPick.current = selectComment; });

  // While pinning, the next map click is the comment's place, and names the feature under it.
  useEffect(() => {
    if (!map || !pins.pinning) return undefined;
    const onClick = (e) => {
      const ed = layoutRef.current?.editor;
      const hit = ed ? (map.queryRenderedFeatures?.(e.point, { layers: [ed.id("fill"), ed.id("outline")] }) ?? []).find((h) => h?.properties?.id) : null;
      const f = hit ? ed.getFeature(hit.properties.id) : null;
      setPinState({ revNo, pinning: false, selected: pins.selected,
        place: { lngLat: [e.lngLat.lng, e.lngLat.lat], featureId: f?.id ?? null, featureName: f ? featureName(f, templatesById) : null } });
    };
    map.on("click", onClick);
    return () => map.off("click", onClick);
  }, [map, pins.pinning, pins.selected, revNo, templatesById]);

  // The layout yields the map while the perimeter is drawn, a pad is picked, or a pin is placed.
  useEffect(() => { layout?.editor.setActive(!interacting && !pins.pinning); }, [layout, interacting, pins.pinning]);

  /* ── the panel ─────────────────────────────────────────────────────────── */

  const revision = approval?.revisions?.find((r) => r.rev_no === revNo) ?? null;
  const box = decisionBoxFor({ approval, viewedRevNo: revNo, meId });
  const mapCanEdit = canEdit && onCurrent;
  const editor = layout?.editor ?? null;
  // Imagery | Basemap | Paper, the user's (mapView.js); Paper's grid is the layout's own frame.
  const mapView = useMapView();
  useEditorDrawingStyle(editor, mapView, layout?.checks ?? null);
  const selected = selectedId && editor ? editor.getFeature(selectedId) : null;
  const afterLifecycle = () => { setViewed(null); refresh(); };

  return (
    // h-full: the shell's <main> is a block box, where flex-1 does nothing and
    // the row stopped at its content's height. The panel and map now fill it.
    <div className="flex h-full min-h-0">
      <NavEnd>
        {plan.archived_at
          ? <span className="op-chip" data-testid="pp-nav-archived" title={`Archived ${formatUpdated(plan.archived_at)}`}>Archived</span>
          : <CompactStepper status={shownStatus({ plan, approval, revNo })} />}
      </NavEnd>
      <SidePanel label="Plan" head={
        <PlanHead plan={plan} canEdit={canEdit && !editing} onEdit={() => setEditing(true)} onArchived={onArchived} onNavigate={onNavigate}
          intended={intendedLandownerLine(plan, landowner.cases)}
          viewing={{ revNo, current: currentRev, frozen, onBack: () => setViewed(null) }}
          approval={approval} onStarted={afterLifecycle} onReport={refresh}
          versions={approvalError
            ? <ReadFailed what="the versions" onRetry={refresh} testid="pp-versions-failed" />
            : <VersionsStrip approval={approval} viewedRevNo={revNo} onView={(n) => setViewed(n === currentRev ? null : n)} />}
          submit={onCurrent && (
            <SubmitButton planId={plan.id} planNumber={plan.plan_number} approval={approval} checks={checks} canEdit={canEdit}
              approvalError={approvalError} onRetry={refresh}
              onRerun={canRerun ? rerunChecks : null} rerun={rerun}
              onSubmitted={() => { setTab("review"); refresh(); }} />
          )} />
      }>
        {notice && <p className="rounded border border-bg-border px-2 py-1.5 text-[12.5px] text-fg-primary" role="status">{notice}</p>}
        {/* The form is in the body, which scrolls; in the fixed head it was clipped in a short window. */}
        {editing ? (
          <EditDetails plan={plan} relationships={landowner.relationships} relationshipsFailed={Boolean(landowner.relationshipsError)}
            onCancel={() => setEditing(false)} onSaved={(r) => { adopt(r); setEditing(false); }} />
        ) : (
          <>
            <div className="flex border-b border-bg-border" role="tablist" aria-label="Plan">
              {PLAN_TABS.map((t) => (
                <button key={t} type="button" role="tab" aria-selected={tab === t} className={tabClass(tab === t)} onClick={() => setTab(t)}>
                  {TAB_LABELS[t]}{t === "wells" && <> <span className="op-mono text-fg-secondary">{plan.revision.wells.length}</span></>}
                </button>
              ))}
            </div>
            {tab === "review" && (
              <div className="flex flex-col gap-4" data-testid="pp-review">
                {approvalError && <ReadFailed what="the plan's approval record" error={approvalError} onRetry={refresh} testid="pp-approval-failed" />}
                <MapPinLayers overlays={overlays} onOverlay={onOverlay} />
                <RouteRow plan={plan} approval={approval} canEdit={canEdit} onChanged={refresh} />
                {revision && <ApprovalChain revision={revision} />}
                {box && <DecisionBox planId={plan.id} box={box} onDecided={refresh} />}
                {onCurrent && <LifecycleActions planId={plan.id} approval={approval} onDone={afterLifecycle} />}
                <LandownerSection plan={plan} revNo={revNo} revision={revision}
                  cases={landowner.cases} casesError={landowner.casesError}
                  relationships={landowner.relationships} relationshipsError={landowner.relationshipsError}
                  onRetry={reloadLandowner} inView={inView} selectedId={finding?.id ?? null}
                  onSelectFinding={selectFinding} onSubmitted={reloadLandowner} />
                <Comments planId={plan.id} revNo={revNo} list={comments} selectedId={pins.selected} onSelect={selectComment}
                  pinning={pins.pinning} onPinning={(on) => setPins({ pinning: on })} place={pins.place}
                  onClearPlace={() => setPins({ place: null, pinning: false })} onChanged={reloadComments}
                  loadError={commentsError} onRetry={reloadComments} />
              </div>
            )}
            {tab === "layout" && (
              <div className="flex flex-col gap-4" data-testid="pp-layout-tab">
                {library.failed && <ReadFailed what="the templates this layout is drawn with" onRetry={retryTemplates} testid="pp-templates-read-failed" />}
                {rulesFailed.length > 0 && <ReadFailed what="the clearance rules this layout is checked against" onRetry={retryRules} testid="pp-rules-read-failed" />}
                <LayersTab editor={editor} hidden={hidden} onToggle={(layer, visible) => {
                  editor?.setLayerVisibility(layer, visible);
                  setHidden((h) => { const n = new Set(h); if (visible) n.delete(layer); else n.add(layer); return n; });
                }} overlays={overlays} pinLayers onOverlay={onOverlay} />
                <ChecksTab checks={checks} rulesById={rulesById} onRerun={canRerun ? rerunChecks : null} rerun={rerun}
                  onPick={(r) => { frameResult(layout, map, r); rerender(); }} />
              </div>
            )}
            {tab === "details" && <Details plan={plan} />}
            {tab === "wells" && <Wells plan={plan} canEdit={canEdit} onSaved={adopt} />}
            {tab === "templates" && <TemplatesTab plan={plan} canEdit={canEdit && onCurrent && !plan.archived_at} onPinned={adopt} />}
          </>
        )}
      </SidePanel>

      <div className="relative min-w-0 flex-1" data-testid="pp-plan-map">
        <PlanMap plan={plan} view={onCurrent ? plan.revision : fdata?.revision ?? null} canEdit={mapCanEdit}
          onMapReady={onMapReady} onInteracting={setInteracting} mapView={mapView} gridEpsg={editor?.frame?.epsg ?? null}
          hud={{
            topRight: selected && (
              <div key="feature" className="max-h-[45vh] w-[290px] max-w-full overflow-y-auto">
                <FeatureDetails feature={selected} templatesById={templatesById} wells={plan.revision.wells}
                  onClose={() => editor?.clearSelection()} />
              </div>
            ),
            bottomLeft: overlays.layout && fdata && <LayoutChecksPanel key="checks" checks={checks} rulesById={rulesById} />,
            bottomCenter: layoutError && <ReadFailed key="layout-failed" what={`v${revNo}'s layout`} error={layoutError}
              onRetry={() => setRevAttempt((n) => n + 1)} testid="pp-layout-read-failed" className={MAP_NOTE} />,
          }}
          onSaved={(r) => {
            adopt(r);
            setNotice(r?.imagery_cleared ? "The imagery pin was cleared because the perimeter changed. Pin imagery again for the new outline." : null);
          }} />
      </div>
    </div>
  );
}

/* ── the panel's head: who the plan is, Submit, the actions ── */

// Mockup 4: breadcrumb, title, status pill and saved time; mockup 5 adds the
// stepper (now in the strip, NavEnd above) and Submit. The plan number is in the breadcrumb only. The buttons
// are headActions'.
function PlanHead({ plan, canEdit, onEdit, onArchived, onNavigate, viewing, submit, versions = null, approval = null, onStarted, onReport, intended = null }) {
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const onCurrent = !viewing || viewing.revNo === viewing.current;
  const shownRev = viewing?.revNo ?? plan.current_revision;
  const actions = headActions({ canEdit, approval, onCurrent, viewedRevNo: shownRev });

  async function start() {
    if (!window.confirm(startRevisionConfirm(plan.current_revision))) return;
    setBusy(true);
    try { const r = await startRevision(plan.id); setDone(startedText(r)); setError(null); onStarted?.(r); }
    catch (err) { setDone(null); setError(refusalText(err)); }
    finally { setBusy(false); }
  }

  async function archive() {
    if (!window.confirm(`Archive ${plan.plan_number}? An archived plan can't be changed.`)) return;
    try { onArchived(await archivePlan(plan.id)); setError(null); } catch (err) { setError(refusalText(err)); }
  }

  // The version on screen, as the full file (S6b), or as its issued report
  // (S7c); anyone who can see the page.
  async function saveFile(read) {
    try {
      const { blob, filename } = await read();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      setError(null);
    } catch (err) { setError(refusalText(err)); }
  }
  const download = () => saveFile(() => exportPlanGeoJson(plan.id, shownRev, { profile: "full" }));
  const downloadReport = () => saveFile(() => getPlanReport(plan.id, shownRev));

  // An issued version without a report (S7d): made once, for the version on
  // screen, which stays on screen; the approval read then offers Download.
  async function generateReport() {
    if (!window.confirm(generateReportConfirm(shownRev))) return;
    setBusy(true);
    try { const r = await generatePlanReport(plan.id, shownRev); setDone(generatedReportText(r)); setError(null); onReport?.(r); }
    catch (err) { setDone(null); setError(refusalText(err)); }
    finally { setBusy(false); }
  }

  return (
    <>
      <nav className="op-meta flex items-center gap-1" aria-label="Breadcrumb">
        <button type="button" className="hover:text-fg-primary" onClick={() => onNavigate("/padplanner")}>{PADPLANNER_DISPLAY_NAME}</button>
        <span aria-hidden="true">/</span>
        <button type="button" className="hover:text-fg-primary" onClick={() => onNavigate("/padplanner/plans")}>Plans</button>
        <span aria-hidden="true">/</span>
        <span className="op-mono text-fg-primary" data-testid="pp-plan-number">{plan.plan_number}</span>
      </nav>
      {plan.archived_at && (
        <p className="op-chip self-start" data-testid="pp-archived">Archived {formatUpdated(plan.archived_at)}{plan.archived_by_display_name ? ` by ${plan.archived_by_display_name}` : ""}</p>
      )}
      <div className="flex flex-col gap-1.5">
        <h1 className="font-display text-[19px] font-semibold leading-tight text-fg-primary">{plan.title}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status={plan.status} archived={Boolean(plan.archived_at)} suffix={`v${plan.current_revision}`} />
          <span className="op-meta" data-testid="pp-saved">{formatSaved(plan.updated_at)}</span>
        </div>
        {intended && (
          <p className="text-[12.5px] text-fg-primary" data-testid="pp-intended-landowner">
            {intended.text}
            {intended.reason && <span className="op-meta block" data-testid="pp-intended-landowner-reason">{intended.reason}</span>}
          </p>
        )}
        <div className="flex flex-wrap gap-2 pt-1" data-testid="pp-head-actions">
          {actions.includes("open_editor") && (
            <button type="button" className="op-btn op-btn--primary op-btn--sm" data-testid="pp-open-editor"
              onClick={() => onNavigate("/padplanner/plans/$planId/edit", { planId: plan.id })}>Open editor</button>
          )}
          {actions.includes("edit_details") && <button type="button" className="op-btn op-btn--sm" onClick={onEdit}>Edit details</button>}
          {actions.includes("archive") && <button type="button" className="op-btn op-btn--sm" onClick={archive}>Archive</button>}
          {actions.includes("start_revision") && (
            <button type="button" className="op-btn op-btn--primary op-btn--sm" disabled={busy} data-testid="pp-start-revision" onClick={start}>
              Start revision {plan.current_revision + 1}
            </button>
          )}
          {actions.includes("download_geojson") && (
            <button type="button" className="op-btn op-btn--sm" data-testid="pp-download-geojson" onClick={download}
              title={`v${shownRev}, every feature`}>Download GeoJSON</button>
          )}
          {actions.includes("download_report") && (
            <button type="button" className="op-btn op-btn--sm" data-testid="pp-download-report" onClick={downloadReport}
              title={reportTitle(shownRev, reportFor(approval, shownRev))}>Download report</button>
          )}
          {actions.includes("generate_report") && (
            <button type="button" className="op-btn op-btn--sm" disabled={busy} data-testid="pp-generate-report" onClick={generateReport}
              title={`v${shownRev} was issued before plan reports were stored`}>Generate report</button>
          )}
        </div>
        {done && <p className="op-meta" role="status" data-testid="pp-head-done">{done}</p>}
        {submit}
        {viewing && viewing.revNo !== viewing.current && (
          <p className="op-meta flex flex-wrap items-center gap-2" data-testid="pp-viewing-older">
            {olderVersionText(viewing.revNo, viewing.current)}
            <button type="button" className="text-accent-action hover:underline" onClick={viewing.onBack}>Show v{viewing.current}</button>
          </p>
        )}
        {viewing?.frozen && <p className="op-meta" data-testid="pp-frozen">{FROZEN_TEXT}</p>}
      </div>
      {versions}
      {error && <p className="text-sm text-accent-red" role="alert">{error}</p>}
    </>
  );
}

function EditDetails({ plan, relationships, relationshipsFailed, onCancel, onSaved }) {
  const planProjects = usePlanProjects();
  const [title, setTitle] = useState(plan.title);
  const [planType, setPlanType] = useState(plan.plan_type);
  const [targetDate, setTargetDate] = useState(plan.target_date ?? "");
  const [projectId, setProjectId] = useState(plan.project?.id ?? "");
  const [landownerId, setLandownerId] = useState(plan.intended_landowner?.org_id ?? null);
  const [error, setError] = useState(null);
  // The plan's own project is offered even when the list lacks it (archived
  // since it was set): keeping it is not a change, so it is never sent.
  const options = [...(planProjects.projects ?? [])];
  if (plan.project && !options.some((p) => p.id === plan.project.id)) options.unshift({ id: plan.project.id, name: plan.project.name ?? "This plan's project" });

  async function save(e) {
    e.preventDefault();
    const body = {};
    if (title.trim() !== plan.title) body.title = title.trim();
    if (planType !== plan.plan_type) body.plan_type = planType;
    if ((targetDate || null) !== (plan.target_date ?? null)) body.target_date = targetDate || null;
    if ((projectId || null) !== (plan.project?.id ?? null)) body.project_id = projectId || null;
    if (landownerId === "") { setError(INTENDED_CHOOSE_TEXT); return; }
    if (landownerId !== (plan.intended_landowner?.org_id ?? null)) body.intended_landowner_org_id = landownerId;
    if (Object.keys(body).length === 0) { onCancel(); return; }
    try { onSaved(await updatePlan(plan.id, body)); } catch (err) { setError(refusalText(err)); }
  }

  return (
    <form className="flex flex-col gap-2.5" onSubmit={save} aria-label="Edit details">
      <label className="flex flex-col gap-1"><span className="op-label uppercase">Title</span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} className={field} /></label>
      <label className="flex flex-col gap-1"><span className="op-label uppercase">Plan type</span>
        <select value={planType} onChange={(e) => setPlanType(e.target.value)} className={field}>
          {PLAN_TYPES.map((t) => <option key={t} value={t}>{PLAN_TYPE_LABELS[t]}</option>)}
        </select></label>
      <label className="flex flex-col gap-1"><span className="op-label uppercase">Target date</span>
        <input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} className={field} /></label>
      <label className="flex flex-col gap-1"><span className="op-label uppercase">Project</span>
        <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className={field}>
          <option value="">No project</option>
          {options.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select></label>
      {planProjects.failed && <p className="text-xs text-fg-secondary">{PROJECTS_FAILED_TEXT}</p>}
      <IntendedLandownerField relationships={relationships} failed={relationshipsFailed} value={landownerId} onChange={setLandownerId}
        current={plan.intended_landowner} lockedReason={intendedLocked(plan)} />
      {error && <p className="text-sm text-accent-red" role="alert">{error}</p>}
      <div className="flex gap-2">
        <button type="submit" className="op-btn op-btn--primary op-btn--sm">Save</button>
        <button type="button" className="op-btn op-btn--sm" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

/* ── Details ────────────────────────────────────────────────────────────── */

function Details({ plan }) {
  const rev = plan.revision;
  const rows = [
    ["Type", PLAN_TYPE_LABELS[plan.plan_type] ?? plan.plan_type],
    ["Target", formatDate(plan.target_date) ?? "Not set"],
    ["Owner", plan.owner?.display_name ?? (plan.owner ? "Unknown user" : "No owner")],
    ["Project", plan.project ? (plan.project.name ?? "Unnamed project") : "No project"],
    ["Created", `${plan.created_by.display_name} · ${formatUpdated(plan.created_at)}`],
  ];
  return (
    <div className="flex flex-col gap-4">
      <dl className="grid grid-cols-[88px_1fr] gap-x-3 gap-y-1.5 text-[13px]">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="op-label uppercase">{k}</dt>
            <dd className="text-fg-primary">{v}</dd>
          </div>
        ))}
      </dl>
      <div className="flex flex-col gap-1">
        <SectionLabel>Perimeter</SectionLabel>
        <p className="text-[13px]" data-testid="pp-perimeter-readout">
          {rev.perimeter
            ? `${formatAcres(rev.perimeter_area_m2)} · ${rev.perimeter_vertices} corners${rev.pad_entity_id ? " · from a detected pad" : ""}`
            : <span className="op-meta">Not set</span>}
        </p>
      </div>
      <div className="flex flex-col gap-1">
        <SectionLabel>Imagery</SectionLabel>
        <p className="text-[13px]" data-testid="pp-imagery-pin">
          {rev.imagery ? `Pinned: ${imageryLabel(rev.imagery)}` : "Not pinned"}
        </p>
        {!rev.perimeter && <p className="op-meta">Set the perimeter to see the imagery that covers it.</p>}
      </div>
    </div>
  );
}

/* ── the map, its toolbar, the pending bar and the time strip ───────────── */

/**
 * `view` is the revision shown ({perimeter, imagery}: the plan's own, or GET
 * …/revisions/:rev_no/features' for another); `canEdit` is false off the
 * current one. `hud` is the page's own overlays by MapHud slot (the feature
 * details, the Layout checks panel, a failed layout read); the map adds its
 * toolbar (topCenter), the pending bar, errors (bottomCenter) and the time strip.
 */
function PlanMap({ plan, view, canEdit, onSaved, onMapReady = null, onInteracting = null, hud = {}, mapView = null, gridEpsg = null }) {
  const rev = { ...plan.revision, perimeter: view?.perimeter ?? null, imagery: view?.imagery ?? null };
  const mapRef = useRef(null);
  // Align grid to pad (padAlign.js): the same choice as in the editor, here for Paper's grid alone (nothing snaps).
  const [padChoice, setPadChoice] = usePadAlign(plan.id);
  const epsg = gridEpsg ?? rev.local_epsg ?? provisionalEpsg(rev.perimeter);
  const gridAxis = useMemo(() => (isUtmEpsg(epsg) ? padAxis(rev.perimeter, createFrame(epsg), padChoice) : null),
    [epsg, rev.perimeter, padChoice]);
  // {geometry, source: 'drawn'|'file'|'pad', label, observationId?, area_m2?, vertices?}
  const [pending, setPending] = useState(null);
  const [picking, setPicking] = useState(false);
  const [showPads, setShowPads] = useState(false);
  const [drawMode, setDrawMode] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const hasPerimeter = Boolean(rev.perimeter);
  const pinned = rev.imagery?.dataset_key ?? null;
  // The chip picked, forgotten whenever the pin changes; undefined: none picked.
  const [cursor, setCursor] = useState({ pin: pinned, key: undefined });

  // The lanes and the opening capture are the editor's too (usePlanImagery.js).
  const { lanes: shownLanes, error: lanesError, retry: retryLanes } = usePlanLanes(plan.id, rev.perimeter);
  // With no chip picked the map opens on the pin, or with none the newest
  // collect: shown, not pinned (the Pin button is offered for it).
  const picked = cursor.pin === pinned ? cursor.key : undefined;
  const cursorKey = picked !== undefined ? picked : openingImagery(pinned, shownLanes).key;

  // The layout pauses while the map is drawn on or a pad is picked.
  useEffect(() => { onInteracting?.(Boolean(drawMode) || picking); }, [onInteracting, drawMode, picking]);

  const onDraw = useCallback((geometry) => {
    setPending(geometry ? { geometry, source: "drawn", label: "drawn on the map" } : null);
  }, []);

  async function onPadPick(observationId) {
    setPicking(false);
    setError(null);
    try {
      const pad = await getPad(observationId);
      if (!pad.usable_as_perimeter) { setError(refusalText(pad.refusal)); return; }
      mapRef.current?.clearDraw();
      setPending({ geometry: pad.geometry, source: "pad", label: "a detected pad", observationId, area_m2: pad.area_m2, vertices: pad.vertices });
    } catch (err) { setError(refusalText(err)); }
  }

  async function onFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError(null);
    setBusy(true);
    try {
      const r = await parsePerimeterFile(file);
      mapRef.current?.clearDraw();
      setPending({ geometry: r.geometry, source: "file", label: file.name, area_m2: r.area_m2, vertices: r.vertices });
    } catch (err) { setError(refusalText(err)); } finally { setBusy(false); }
  }

  function discard() {
    mapRef.current?.clearDraw();
    setPending(null);
  }

  async function savePending() {
    setBusy(true);
    setError(null);
    try {
      // perimeter_source is our word for the audit row; the server records it
      // as client-declared and decides nothing by it.
      const body = pending.source === "pad" ? { pad_observation_id: pending.observationId }
        : { perimeter: pending.geometry, perimeter_source: pending.source === "file" ? "uploaded" : "drawn" };
      onSaved(await updateRevision(plan.id, body));
      mapRef.current?.clearDraw();
      setPending(null);
    } catch (err) { setError(refusalText(err)); } finally { setBusy(false); }
  }

  async function pin(key) {
    setError(null);
    try { onSaved(await updateRevision(plan.id, { imagery: key ? { dataset_key: key } : null })); } catch (err) { setError(refusalText(err)); }
  }

  const cursorIsCollect = Boolean(shownLanes?.collects?.some((c) => c.dataset_key === cursorKey));
  const pinActions = canEdit ? (
    <>
      {cursorKey && cursorKey !== pinned && cursorIsCollect && (
        <button type="button" className="op-btn op-btn--primary op-btn--sm" onClick={() => pin(cursorKey)}>Pin</button>
      )}
      {pinned && <button type="button" className="op-btn op-btn--sm" onClick={() => pin(null)}>Unpin</button>}
    </>
  ) : null;
  const shape = pending?.geometry ?? rev.perimeter;
  const btn = "op-btn op-btn--sm";

  return (
    <>
      <PadPlannerMap ref={mapRef} onDrawMode={setDrawMode}
        fitKey={plan.id} perimeter={rev.perimeter} onMapReady={onMapReady}
        footprint={{ role: "planPage", include: includeForCapture({ pin: rev.imagery, lanes: shownLanes, key: cursorKey }),
          withReference: referenceForCapture({ pin: rev.imagery, lanes: shownLanes, key: cursorKey }) }}
        view={mapView} gridEpsg={epsg} gridAxis={gridAxis}
        viewAlign={{ checked: Boolean(padChoice), disabled: !rev.perimeter, onChange: (on) => setPadChoice(on ? { mode: "long" } : null) }}
        draft={pending?.source === "drawn" ? null : pending?.geometry ?? null}
        editable={canEdit}
        onDraw={canEdit ? onDraw : undefined}
        showPads={showPads}
        onPadPick={canEdit && picking ? onPadPick : null}
        rasterKey={cursorKey} rasterCredit={creditForCapture({ pin: rev.imagery, lanes: shownLanes, key: cursorKey })}
        hint={canEdit && !hasPerimeter && !pending ? "Draw the perimeter, upload a file, or use a detected pad." : null}
        readout={[
          { label: "Area", value: pending ? formatAcres(pending.area_m2) : (hasPerimeter ? formatAcres(rev.perimeter_area_m2) : null) },
          { label: "Corners", value: pending ? (pending.vertices ? String(pending.vertices) : null) : (hasPerimeter ? String(rev.perimeter_vertices) : null) },
        ]}
        showCursor
        scaleBar
        hud={{
          ...hud,
          topCenter: [
            <div key="toolbar" className="flex flex-wrap items-center justify-center gap-1 rounded-md border border-bg-border bg-bg/90 p-1 shadow"
              data-testid="pp-plan-toolbar">
              {canEdit && (
                <div className="flex flex-wrap items-center gap-1" data-testid="pp-draw-toolbar">
                  <button type="button" className={btn} aria-pressed={drawMode === "polygon"}
                    onClick={() => { setPicking(false); setPending(null); mapRef.current?.startDraw(); }}>Draw</button>
                  <button type="button" className={btn} aria-pressed={drawMode === "edit"} disabled={!editablePolygon(shape) || pending?.source === "pad" || pending?.source === "file"}
                    title={shape && !editablePolygon(shape) ? "A perimeter in several parts can be replaced but not edited by vertex." : undefined}
                    onClick={() => mapRef.current?.startEdit()}>Edit vertices</button>
                  {drawMode && <button type="button" className={btn} onClick={discard}>Cancel</button>}
                  <label className={`${btn} cursor-pointer`}>
                    Upload
                    <input type="file" className="hidden" accept=".geojson,.json,.kml,.kmz,.zip" onChange={onFile} disabled={busy} aria-label="Upload perimeter" />
                  </label>
                  <button type="button" className={btn} aria-pressed={picking} onClick={() => setPicking((v) => !v)}>Use detected pad</button>
                  <span className="mx-1 h-5 w-px bg-bg-border" aria-hidden="true" />
                </div>
              )}
              <label className="op-meta flex items-center gap-1 px-1.5">
                <input type="checkbox" checked={showPads} onChange={(e) => setShowPads(e.target.checked)} /> Detected pads
              </label>
            </div>,
            ...[].concat(hud.topCenter ?? []),
          ],
          bottomCenter: [
            ...[].concat(hud.bottomCenter ?? []),
            pending && (
              <div key="pending" className="flex flex-wrap items-center gap-2 rounded-md border border-bg-border bg-bg/95 px-3 py-2 shadow-lg" data-testid="pp-pending">
                <span className="text-[13px]">
                  Unsaved perimeter from {pending.label}
                  {pending.area_m2 ? ` · ${formatAcres(pending.area_m2)}` : ""}{pending.vertices ? ` · ${pending.vertices} corners` : ""}
                  {pending.source === "file" ? ". Check the outline on the map before saving." : ""}
                </span>
                <button type="button" className="op-btn op-btn--primary op-btn--sm" disabled={busy} onClick={savePending}>Save perimeter</button>
                <button type="button" className="op-btn op-btn--sm" onClick={discard}>Discard</button>
              </div>
            ),
            error && <p key="error" className="rounded bg-bg/95 px-3 py-1.5 text-sm text-accent-red shadow" role="alert">{error}</p>,
            lanesError && <ReadFailed key="lanes" what="the imagery dates" onRetry={retryLanes} testid="pp-lanes-failed" className={MAP_NOTE} />,
          ],
        }}
        bottom={shownLanes && ((strip) => (
          <ImageryStrip onChip={selectAndZoom((key) => setCursor({ pin: pinned, key }), strip)} rows={sitePlannerRows({ lanes: shownLanes, cursorKey,
            markKey: pinned, emptyText: imageryEmptyText(shownLanes.imagery_entitled), end: pinActions, ...strip })} />
        ))} />
    </>
  );
}

/* ── Wells ──────────────────────────────────────────────────────────────── */

function Wells({ plan, canEdit, onSaved }) {
  const wells = plan.revision.wells;
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [manualLabel, setManualLabel] = useState("");
  const [error, setError] = useState(null);

  async function search(e) {
    e.preventDefault();
    setError(null);
    try { setResults(await lookupWells(q)); } catch (err) { setResults(null); setError(refusalText(err)); }
  }
  async function add(api, body) {
    setError(null);
    try { onSaved(await putWell(plan.id, api, body)); } catch (err) { setError(refusalText(err)); }
  }
  async function remove(api) {
    setError(null);
    try { onSaved(await deleteWell(plan.id, api)); } catch (err) { setError(refusalText(err)); }
  }

  const onPlan = new Set(wells.map((w) => w.api10));
  const digits = q.replace(/[\s-]/g, "");
  const fullNumber = /^\d{10}$|^\d{14}$/.test(digits) && digits.startsWith("42");

  return (
    <div className="flex flex-col gap-3">
      {wells.length === 0 && <p className="op-meta">No wells on this plan.</p>}
      <ul className="flex flex-col gap-1">
        {wells.map((w) => (
          <li key={w.api10} className="flex items-center justify-between gap-2 text-[13px]" data-testid="pp-well">
            <span><span className="op-mono">{formatApi10(w.api10)}</span>{w.label ? ` · ${w.label}` : ""}
              <span className="op-meta"> · {w.source === "rrc" ? "RRC" : "planned"}</span></span>
            {canEdit && <button type="button" className="op-meta hover:text-fg-primary" onClick={() => remove(w.api10)}>Remove</button>}
          </li>
        ))}
      </ul>

      {canEdit && (
        <>
          <form className="flex gap-2" onSubmit={search}>
            <input value={q} onChange={(e) => setQ(e.target.value)} aria-label="API number" placeholder="API number, e.g. 42-383-12345"
              className={`min-w-0 flex-1 ${field}`} />
            <button type="submit" className="op-btn op-btn--sm">Find</button>
          </form>
          {results && (
            <ul className="flex flex-col gap-1" data-testid="pp-well-results">
              {results.wells.length === 0 && <li className="op-meta">No RRC well has that number.</li>}
              {results.wells.map((w) => (
                <li key={w.api10} className="flex items-center justify-between gap-2 text-[13px]">
                  <span><span className="op-mono">{formatApi10(w.api10)}</span>
                    <span className="op-meta"> · {[w.operator, w.lease_name && `${w.lease_name}${w.well_no ? ` #${w.well_no}` : ""}`, w.status_norm].filter(Boolean).join(" · ")}</span></span>
                  {onPlan.has(w.api10)
                    ? <span className="op-meta">On plan</span>
                    : <button type="button" className="op-btn op-btn--sm" onClick={() => add(w.api10, { source: "rrc" })}>Add</button>}
                </li>
              ))}
              {results.truncated && <li className="op-meta">More match. Type more digits.</li>}
            </ul>
          )}
          {results && results.wells.length === 0 && fullNumber && (
            <div className="flex flex-wrap items-center gap-2" data-testid="pp-manual-well">
              <input value={manualLabel} onChange={(e) => setManualLabel(e.target.value)} maxLength={120} aria-label="Well label"
                placeholder="Label, e.g. Unit 7H" className={field} />
              <button type="button" className="op-btn op-btn--sm" onClick={() => add(digits, { source: "manual", label: manualLabel || null })}>
                Add as planned well
              </button>
            </div>
          )}
        </>
      )}
      {error && <p className="text-sm text-accent-red" role="alert">{error}</p>}
    </div>
  );
}
